import { useState, useRef, useEffect } from 'react';
import { useLang } from '@/components/TranslatedText';
import { useAppDispatch } from '@/state/store';
import { toggleDebugMenu } from '@/state/features/app';

export default function LanguageCycler() {
    const dispatch = useAppDispatch();
    const { langs, setLang } = useLang();
    const [isCycling, setIsCycling] = useState(false);
    const [isPlusEnabled, setIsPlusEnabled] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const indexRef = useRef(0);
    const manualIndexRef = useRef(0);

    const handleStop = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setIsCycling(false);
    };

    const handleStart = (seconds: number) => {
        if (!langs.length) return;
        handleStop();
        indexRef.current = 0;
        setIsCycling(true);
        dispatch(toggleDebugMenu());
        intervalRef.current = setInterval(() => {
            const lang = langs[indexRef.current];
            console.log(`[LanguageCycler] Setting language: ${lang.langISO} (${lang.displayName})`);
            setLang(lang.langISO);
            indexRef.current++;
            if (indexRef.current >= langs.length) {
                console.log('[LanguageCycler] Finished cycling through all languages');
                handleStop();
            }
        }, seconds * 1000);
    };

    const langsRef = useRef(langs);
    useEffect(() => {
        langsRef.current = langs;
    }, [langs]);
    const setLangRef = useRef(setLang);
    useEffect(() => {
        setLangRef.current = setLang;
    }, [setLang]);

    useEffect(() => {
        if (!isPlusEnabled) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== '+' && e.key !== '-') return;
            if (!langsRef.current.length) return;
            if (e.key === '+') manualIndexRef.current++;
            if (e.key === '-') manualIndexRef.current--;
            const index =
                ((manualIndexRef.current % langsRef.current.length) + langsRef.current.length) %
                langsRef.current.length;
            const lang = langsRef.current[index];
            console.log(`[LanguageCycler] Manual: ${lang.langISO} (${lang.displayName})`);
            setLangRef.current(lang.langISO);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isPlusEnabled]);

    return (
        <section>
            <h3>Auto cycle languages</h3>
            <button onClick={() => handleStart(1)} disabled={!langs.length}>
                1s
            </button>
            <button onClick={() => handleStart(5)} disabled={!langs.length}>
                5s
            </button>
            <button onClick={() => handleStart(10)} disabled={!langs.length}>
                10s
            </button>
            <button onClick={() => handleStart(30)} disabled={!langs.length}>
                30s
            </button>
            <button onClick={handleStop} disabled={!isCycling}>
                Stop
            </button>
            <div>
                <label>
                    <input
                        type="checkbox"
                        checked={isPlusEnabled}
                        onChange={(e) => {
                            manualIndexRef.current = 0;
                            setIsPlusEnabled(e.target.checked);
                        }}
                    />{' '}
                    +/- keys step through languages
                </label>
            </div>
        </section>
    );
}
